import { matchRoutes, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'

import { MobileMainLayout } from '@layouts/main'

import { AddUserPage } from '@modules/admin/pages/add-user'
import { EditUserPage } from '@modules/admin/pages/edit-user'

import { AddPage } from '@modules/project/pages/add'
import { EditPage } from '@modules/project/pages/edit'

import { AddBuildingPage } from '@modules/project-zoning/pages/add-building'
import { EditBuildingPage } from '@modules/project-zoning/pages/edit-building'

import { AddFloorPage } from '@modules/project-zoning/pages/add-floor'
import { EditFloorPage } from '@modules/project-zoning/pages/edit-floor'

import { ManageZonesPage } from '@modules/project-zoning/pages/manage-zones'

import { AddCompanyPage } from '@modules/project-companies/pages/add-company'
import { EditCompanyPage } from '@modules/project-companies/pages/edit-company'

import { AddTaskTemplatePage } from '@modules/project-library/pages/add-task-template'
import { EditTaskTemplatePage } from '@modules/project-library/pages/edit-task-template'

import { AddSequenceTemplatePage } from '@modules/project-library/pages/add-sequence-template'
import { EditSequenceTemplatePage } from '@modules/project-library/pages/edit-sequence-template'

import { AddPlanPage } from '@modules/project-plans/pages/add'
import { EditPlanPage } from '@modules/project-plans/pages/edit'

import { AddDocumentPage } from '@modules/project-documents/pages/add'
import { EditDocumentPage } from '@modules/project-documents/pages/edit'

import { AddUserPage as AddProjectUserPage } from '@modules/project-users/pages/add'
import { EditUserPage as EditProjectUserPage } from '@modules/project-users/pages/edit'

import { GuardAddTaskPage } from '@modules/project-suivi/pages/add-task'
import { GuardEditTaskPage } from '@modules/project-suivi/pages/edit-task'

import { GuardAddFormPage } from '@modules/project-forms/pages/add-form'
import { GuardEditFormPage } from '@modules/project-forms/pages/edit-form'

import { GuardAddObservationPage } from '@modules/project-observations/pages/add-observation'
import { GuardEditObservationPage } from '@modules/project-observations/pages/edit-observation'

import { GuardSettingsPage } from '@modules/settings/pages/settings'

import { useModal } from '@hooks/app/modal.hook'

import { useAppUpdate, usePlatformDetection } from '@utils/hooks'

import { SelectProject } from '../../components/SelectProject'
import { BottomSheetCheckUpdate } from '../../components/BottomSheetCheckUpdate'
import { BottomSheetCheckDownloadApp } from '../../components/BottomSheetCheckDownloadApp'

const HIDDEN_SELECT_ROUTES = ['/:lang/projects/:projectId/plans/editor/:planId']

export const MobileMainPage = (): JSX.Element => {
  const { currentModals } = useModal()

  const location = useLocation()

  const [showUpdateBottomSheet, setShowUpdateBottomSheet] = useState<boolean>(false)
  const [showCheckBottomSheet, setCheckUpdateBottomSheet] = useState<boolean>(false)

  const { performImmediateUpdate, openAppStore, isUpdateAvailable, isCheckingUpdate, checkForUpdates } = useAppUpdate()
  const { isMobileWeb } = usePlatformDetection()

  const routes = HIDDEN_SELECT_ROUTES.map((path) => ({ path }))

  const showSelect = !matchRoutes(routes, location)

  const HeaderComponent = showSelect ? <SelectProject /> : null

  useEffect(() => {
    const initializeUpdateCheck = async (): Promise<void> => {
      setTimeout(() => {
        checkForUpdates()
      }, 3000)
    }

    initializeUpdateCheck()
  }, [checkForUpdates])

  useEffect(() => {
    if (isUpdateAvailable && !isCheckingUpdate) {
      setShowUpdateBottomSheet(true)
    }
  }, [isUpdateAvailable, isCheckingUpdate])

  useEffect(() => {
    if (isMobileWeb) {
      setCheckUpdateBottomSheet(true)
    }
  }, [isMobileWeb])

  const handleUpdateDismiss = (): void => {
    setShowUpdateBottomSheet(false)
  }

  const handleUpdateAction = async (): Promise<void> => {
    try {
      await performImmediateUpdate()
      setShowUpdateBottomSheet(false)
    } catch (error: unknown) {
      console.error('Update failed:', error)
      try {
        await openAppStore()
      } catch (storeError: unknown) {
        console.error('Failed to open app store:', storeError)
      }
      setShowUpdateBottomSheet(false)
    }
  }

  const handleCheckDismiss = (): void => {
    setCheckUpdateBottomSheet(false)
  }

  return (
    <MobileMainLayout
      HeaderComponent={HeaderComponent}
      MainComponent={
        <>
          <Outlet />

          {currentModals.indexOf('add_user') != -1 && <AddUserPage />}
          {currentModals.indexOf('update_user') != -1 && <EditUserPage />}

          {currentModals.indexOf('add_project') != -1 && <AddPage />}
          {currentModals.indexOf('update_project') != -1 && <EditPage />}

          {currentModals.indexOf('add_building') != -1 && <AddBuildingPage />}
          {currentModals.indexOf('update_building') != -1 && <EditBuildingPage />}

          {currentModals.indexOf('add_floor') != -1 && <AddFloorPage />}
          {currentModals.indexOf('update_floor') != -1 && <EditFloorPage />}

          {currentModals.indexOf('manage_zones') != -1 && <ManageZonesPage />}

          {currentModals.indexOf('add_company') != -1 && <AddCompanyPage />}
          {currentModals.indexOf('update_company') != -1 && <EditCompanyPage />}

          {currentModals.indexOf('add_task') != -1 && <GuardAddTaskPage />}
          {currentModals.indexOf('update_task') != -1 && <GuardEditTaskPage />}

          {currentModals.indexOf('add_task_template') != -1 && <AddTaskTemplatePage />}
          {currentModals.indexOf('update_task_template') != -1 && <EditTaskTemplatePage />}

          {currentModals.indexOf('add_sequence') != -1 && <AddSequenceTemplatePage />}
          {currentModals.indexOf('update_sequence') != -1 && <EditSequenceTemplatePage />}

          {currentModals.indexOf('add_plan') != -1 && <AddPlanPage />}
          {currentModals.indexOf('update_plan') != -1 && <EditPlanPage />}

          {currentModals.indexOf('add_document') != -1 && <AddDocumentPage />}
          {currentModals.indexOf('update_document') != -1 && <EditDocumentPage />}

          {currentModals.indexOf('add_project_user') != -1 && <AddProjectUserPage />}
          {currentModals.indexOf('update_project_user') != -1 && <EditProjectUserPage />}

          {currentModals.indexOf('add_form') != -1 && <GuardAddFormPage />}
          {currentModals.indexOf('update_form') != -1 && <GuardEditFormPage />}

          {currentModals.indexOf('add_observation') != -1 && <GuardAddObservationPage />}
          {currentModals.indexOf('update_observation') != -1 && <GuardEditObservationPage />}

          {currentModals.indexOf('settings') != -1 && <GuardSettingsPage />}

          <BottomSheetCheckUpdate
            open={showUpdateBottomSheet}
            onDismiss={handleUpdateDismiss}
            onUpdate={handleUpdateAction}
          />

          <BottomSheetCheckDownloadApp open={showCheckBottomSheet} onDismiss={handleCheckDismiss} />
        </>
      }
    />
  )
}
